/**
 * VpnTopologyView — VPN service architecture diagram.
 *
 * Modelo (Service.config_json.topology):
 *   {
 *     vpn: {
 *       mode: 'standalone' | 'ha_active_active' | 'ha_active_passive',
 *       implementation: 'hardware' | 'software' | 'cloud_native',
 *       gateways: [{ name, server_id, location_id, public_ip, internal_ip,
 *                    vendor, model, is_primary, is_active }],
 *       sites:     [{ name, location_id, internal_cidr, public_endpoint,
 *                     wan_provider, notes }],
 *       tunnels:   [{ from, to, encryption, transport, status, notes }],
 *       remote_access: {
 *         enabled, protocol ('ssl'|'ipsec'|'wireguard'|'openvpn'),
 *         mfa, ad_integrated, max_users, client_subnet,
 *       },
 *       providers: [{ name, kind ('partner'|'cloud'|'isp'), endpoint, notes }],
 *       features:  ['IPSec AES-256','MFA','AD/RADIUS', 'Dual WAN', ...],
 *       wan: { dual_wan, primary_isp, backup_isp, failover_mode },
 *     },
 *     notes: '...'
 *   }
 */
import React from 'react';
import { ShieldCheckIcon, GlobeAltIcon, BuildingOffice2Icon, CloudIcon, UsersIcon } from '@heroicons/react/24/outline';
import {
  useTopologyState, useTopologyCandidates, TopologyHeader,
  SectionBox, NodeCard, ListSection,
  TOPOLOGY_PALETTE, serverLabel, locationLabel,
} from './TopologyShared';

const DEFAULT = {
  vpn: {
    mode: 'standalone',
    implementation: 'hardware',
    gateways: [],
    sites: [],
    tunnels: [],
    remote_access: { enabled: false, protocol: 'ssl', mfa: false, ad_integrated: false, max_users: '', client_subnet: '' },
    providers: [],
    features: [],
    wan: { dual_wan: false, primary_isp: '', backup_isp: '', failover_mode: 'active_passive' },
  },
  notes: '',
};

const MODES = [
  { value: 'standalone',         label: 'Standalone (sin redundancia)' },
  { value: 'ha_active_passive',  label: 'HA Activo / Pasivo' },
  { value: 'ha_active_active',   label: 'HA Activo / Activo' },
];

const IMPL = [
  { value: 'hardware',     label: 'Appliance hardware (FortiGate, Palo Alto, etc.)' },
  { value: 'software',     label: 'Software (OpenVPN, WireGuard, StrongSwan, pfSense VM…)' },
  { value: 'cloud_native', label: 'Cloud-native (Azure VPN Gateway, AWS VGW, GCP)' },
];

const PROTOCOLS = [
  { value: 'ssl',       label: 'SSL VPN' },
  { value: 'ipsec',     label: 'IPSec' },
  { value: 'wireguard', label: 'WireGuard' },
  { value: 'openvpn',   label: 'OpenVPN' },
  { value: 'l2tp',      label: 'L2TP / IPSec' },
];

const PROVIDER_KINDS = [
  { value: 'isp',     label: 'Operador (ISP)' },
  { value: 'partner', label: 'Partner / Cliente' },
  { value: 'cloud',   label: 'Proveedor cloud' },
];

const FAILOVER = [
  { value: 'active_passive', label: 'Activo / Pasivo' },
  { value: 'active_active',  label: 'Balanceado (load balancing)' },
  { value: 'manual',         label: 'Manual / Failover por DNS' },
];

export default function VpnTopologyView({ serviceId, companyId, serviceName }) {
  const state = useTopologyState(serviceId, companyId, DEFAULT);
  const { servers, locations } = useTopologyCandidates(serviceId, companyId);
  const { topology, setTopology, editMode, setEditMode,
          saving, error, loading, save, reload } = state;

  if (loading) return <div className="dv-tab-pane">Cargando topología VPN…</div>;
  if (error)   return <div className="dv-tab-pane" style={{ color: '#f87171' }}>{error}</div>;

  const vpn   = topology.vpn || DEFAULT.vpn;
  const set   = (path, value) => setTopology(t => ({ ...t, vpn: { ...t.vpn, [path]: value } }));
  const setRA = (k, v)        => setTopology(t => ({ ...t, vpn: { ...t.vpn, remote_access: { ...(t.vpn?.remote_access || {}), [k]: v } } }));
  const setWan = (k, v)       => setTopology(t => ({ ...t, vpn: { ...t.vpn, wan: { ...(t.vpn?.wan || {}), [k]: v } } }));

  const isHA = vpn.mode && vpn.mode.startsWith('ha_');
  const accent = isHA ? TOPOLOGY_PALETTE.haAccent : TOPOLOGY_PALETTE.primaryAccent;

  // ── Endpoint options for tunnels (gateways + sites + providers) ──
  const endpointOptions = [
    ...((vpn.gateways || []).map((g, i) => ({ value: `gw:${i}`, label: `GW · ${g.name || `Gateway ${i+1}`}` }))),
    ...((vpn.sites    || []).map((s, i) => ({ value: `site:${i}`, label: `Sede · ${s.name || `Sede ${i+1}`}` }))),
    ...((vpn.providers|| []).map((p, i) => ({ value: `prov:${i}`, label: `Externo · ${p.name || `Proveedor ${i+1}`}` }))),
  ];

  return (
    <div className="dv-tab-pane" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TopologyHeader
        title={serviceName || 'VPN'}
        subtitle="Arquitectura VPN"
        editMode={editMode}
        saving={saving}
        onEdit={() => setEditMode(true)}
        onCancel={() => { setEditMode(false); reload(); }}
        onSave={save}
        onReload={reload}
      />

      {/* ── Modo + implementación ── */}
      <SectionBox title="Modo de despliegue" accent={accent}>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">Modo</label>
            {editMode ? (
              <select className="form-select form-select-sm" value={vpn.mode}
                      onChange={e => set('mode', e.target.value)}>
                {MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            ) : (
              <div style={{ color: TOPOLOGY_PALETTE.textStrong }}>
                {MODES.find(m => m.value === vpn.mode)?.label || vpn.mode}
                {isHA && <span style={{ marginLeft: 8, fontSize: '0.7rem', background: 'rgba(245,158,11,.18)', color: '#fbbf24', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>HA</span>}
              </div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label">Implementación</label>
            {editMode ? (
              <select className="form-select form-select-sm" value={vpn.implementation}
                      onChange={e => set('implementation', e.target.value)}>
                {IMPL.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
              </select>
            ) : (
              <div style={{ color: TOPOLOGY_PALETTE.textStrong }}>
                {IMPL.find(i => i.value === vpn.implementation)?.label || vpn.implementation}
              </div>
            )}
          </div>
        </div>
      </SectionBox>

      {/* ── Visualización ── */}
      {!editMode && (
        <SectionBox title="Diagrama de red" accent={accent}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div style={{ flex: '0 0 240px' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>Gateways</div>
              {(vpn.gateways || []).length === 0 && <div style={{ fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted }}>Sin gateways.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(vpn.gateways || []).map((g, i) => (
                  <NodeCard
                    key={i}
                    icon={ShieldCheckIcon}
                    accent={g.is_primary ? TOPOLOGY_PALETTE.haAccent : (isHA ? TOPOLOGY_PALETTE.secondaryAccent : TOPOLOGY_PALETTE.primaryAccent)}
                    title={g.name || `Gateway ${i+1}`}
                    subtitle={[g.vendor, g.model].filter(Boolean).join(' · ') || null}
                    badges={[
                      ...(g.is_primary ? [{ label: 'Primary', bg: 'rgba(245,158,11,.18)', fg: '#fbbf24' }] : []),
                      ...(g.is_active  ? [{ label: 'Activo',  bg: 'rgba(16,185,129,.18)', fg: '#6ee7b7' }] : (isHA ? [{ label: 'Standby', bg: 'rgba(148,163,184,.18)', fg: '#cbd5e1' }] : [])),
                    ]}
                    footer={
                      <>
                        {g.public_ip   && <div>Pública: {g.public_ip}</div>}
                        {g.internal_ip && <div>Interna: {g.internal_ip}</div>}
                        {g.location_id && <div>Sede: {locationLabel(g.location_id, locations)}</div>}
                        {g.server_id   && <div>Servidor: {serverLabel(g.server_id, servers)}</div>}
                      </>
                    }
                  />
                ))}
              </div>
            </div>

            <div style={{ flex: '0 0 240px' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>Sedes</div>
              {(vpn.sites || []).length === 0 && <div style={{ fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted }}>Sin sedes.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(vpn.sites || []).map((s, i) => (
                  <NodeCard
                    key={i}
                    icon={BuildingOffice2Icon}
                    accent={TOPOLOGY_PALETTE.successAccent}
                    title={s.name || `Sede ${i+1}`}
                    subtitle={s.location_id ? locationLabel(s.location_id, locations) : null}
                    badges={s.wan_provider ? [{ label: s.wan_provider, bg: 'rgba(56,189,248,.18)', fg: '#7dd3fc' }] : []}
                    footer={
                      <>
                        {s.internal_cidr   && <div>LAN: {s.internal_cidr}</div>}
                        {s.public_endpoint && <div>Endpoint: {s.public_endpoint}</div>}
                      </>
                    }
                  />
                ))}
              </div>
            </div>

            <div style={{ flex: '0 0 220px' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>Externos / Cloud</div>
              {(vpn.providers || []).length === 0 && <div style={{ fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted }}>Sin proveedores.</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(vpn.providers || []).map((p, i) => (
                  <NodeCard
                    key={i}
                    icon={p.kind === 'cloud' ? CloudIcon : GlobeAltIcon}
                    accent={p.kind === 'cloud' ? '#38bdf8' : TOPOLOGY_PALETTE.secondaryAccent}
                    title={p.name || `Proveedor ${i+1}`}
                    subtitle={PROVIDER_KINDS.find(k => k.value === p.kind)?.label || p.kind}
                    footer={p.endpoint ? <div>{p.endpoint}</div> : null}
                  />
                ))}
              </div>
            </div>

            <div style={{ flex: '0 0 220px' }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>Acceso remoto</div>
              {vpn.remote_access?.enabled ? (
                <NodeCard
                  icon={UsersIcon}
                  accent={TOPOLOGY_PALETTE.primaryAccent}
                  title={`VPN ${PROTOCOLS.find(p => p.value === vpn.remote_access.protocol)?.label || vpn.remote_access.protocol}`}
                  subtitle={vpn.remote_access.max_users ? `Hasta ${vpn.remote_access.max_users} usuarios` : null}
                  badges={[
                    ...(vpn.remote_access.mfa ? [{ label: 'MFA', bg: 'rgba(16,185,129,.18)', fg: '#6ee7b7' }] : []),
                    ...(vpn.remote_access.ad_integrated ? [{ label: 'AD/RADIUS', bg: 'rgba(99,102,241,.18)', fg: '#a5b4fc' }] : []),
                  ]}
                  footer={vpn.remote_access.client_subnet ? <div>Pool: {vpn.remote_access.client_subnet}</div> : null}
                />
              ) : (
                <div style={{ fontSize: '0.78rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>
                  Acceso remoto deshabilitado.
                </div>
              )}
            </div>
          </div>

          {/* Tunnels list as relationships */}
          {(vpn.tunnels || []).length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: '0.74rem', fontWeight: 700, color: TOPOLOGY_PALETTE.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Túneles ({vpn.tunnels.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
                {vpn.tunnels.map((t, i) => (
                  <div key={i} style={{
                    background: TOPOLOGY_PALETTE.subtleBg, padding: '4px 8px', borderRadius: 4,
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span>{endpointLabel(t.from, vpn)}</span>
                    <span style={{ color: TOPOLOGY_PALETTE.textMuted }}>↔</span>
                    <span>{endpointLabel(t.to, vpn)}</span>
                    {t.encryption && <span style={{ marginLeft: 'auto', background: 'rgba(99,102,241,.18)', color: '#a5b4fc', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>{t.encryption}</span>}
                    {t.status && <span style={{ background: t.status === 'up' ? 'rgba(16,185,129,.18)' : 'rgba(239,68,68,.18)', color: t.status === 'up' ? '#6ee7b7' : '#fca5a5', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>{t.status}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionBox>
      )}

      {/* ── Editables ── */}
      <ListSection
        title="Gateways VPN"
        accent={accent}
        items={vpn.gateways}
        onChange={(v) => set('gateways', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ name: '', is_primary: false, is_active: true }}
        columns={[
          { key: 'name',        label: 'Nombre',     placeholder: 'GW-Madrid-01' },
          { key: 'vendor',      label: 'Vendor',     placeholder: 'Fortinet / Palo Alto / pfSense' },
          { key: 'model',       label: 'Modelo' },
          { key: 'server_id',   label: 'Servidor',   type: 'server' },
          { key: 'location_id', label: 'Sede',       type: 'location' },
          { key: 'public_ip',   label: 'IP pública' },
          { key: 'internal_ip', label: 'IP interna' },
          { key: 'is_primary',  label: 'Primary',    type: 'checkbox' },
          { key: 'is_active',   label: 'Activo',     type: 'checkbox' },
        ]}
        addLabel="Añadir gateway"
        emptyText="Sin gateways VPN definidos."
      />

      <ListSection
        title="Sedes / Redes corporativas"
        accent={TOPOLOGY_PALETTE.successAccent}
        items={vpn.sites}
        onChange={(v) => set('sites', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ name: '' }}
        columns={[
          { key: 'name',            label: 'Nombre',         placeholder: 'Sede Madrid' },
          { key: 'location_id',     label: 'Sede CMDB',      type: 'location' },
          { key: 'internal_cidr',   label: 'LAN (CIDR)',     placeholder: '10.10.0.0/16' },
          { key: 'public_endpoint', label: 'Endpoint público' },
          { key: 'wan_provider',    label: 'Operador WAN' },
        ]}
        addLabel="Añadir sede"
        emptyText="Sin sedes registradas."
      />

      <ListSection
        title="Túneles"
        accent={TOPOLOGY_PALETTE.haAccent}
        items={vpn.tunnels}
        onChange={(v) => set('tunnels', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ encryption: 'AES-256', status: 'up' }}
        columns={[
          { key: 'from',       label: 'Desde',       type: 'select', options: endpointOptions },
          { key: 'to',         label: 'Hacia',       type: 'select', options: endpointOptions },
          { key: 'transport',  label: 'Tipo',        type: 'select', options: ['site-to-site IPSec','SSL','GRE','WireGuard'] },
          { key: 'encryption', label: 'Cifrado',     placeholder: 'AES-256' },
          { key: 'status',     label: 'Estado',      type: 'select', options: ['up','down','degraded'] },
          { key: 'notes',      label: 'Notas' },
        ]}
        addLabel="Añadir túnel"
        emptyText="Sin túneles configurados."
      />

      <ListSection
        title="Proveedores / Cloud / Partners"
        accent={TOPOLOGY_PALETTE.secondaryAccent}
        items={vpn.providers}
        onChange={(v) => set('providers', v)}
        editMode={editMode}
        servers={servers}
        locations={locations}
        defaultRow={{ kind: 'isp' }}
        columns={[
          { key: 'name',     label: 'Nombre',   placeholder: 'Azure / Cliente XYZ / Movistar' },
          { key: 'kind',     label: 'Tipo',     type: 'select', options: PROVIDER_KINDS },
          { key: 'endpoint', label: 'Endpoint', placeholder: 'IP / FQDN / VNet ID' },
          { key: 'notes',    label: 'Notas' },
        ]}
        addLabel="Añadir proveedor"
        emptyText="Sin proveedores externos."
      />

      {/* ── Acceso remoto ── */}
      <SectionBox title="Acceso remoto (VPN de usuarios)" accent={TOPOLOGY_PALETTE.primaryAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!vpn.remote_access?.enabled}
                       onChange={e => setRA('enabled', e.target.checked)} />
                <span>Habilitado</span>
              </label>
            </div>
            <div className="col-md-3">
              <label className="form-label">Protocolo</label>
              <select className="form-select form-select-sm" value={vpn.remote_access?.protocol || 'ssl'}
                      onChange={e => setRA('protocol', e.target.value)}>
                {PROTOCOLS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!vpn.remote_access?.mfa}
                       onChange={e => setRA('mfa', e.target.checked)} />
                <span>MFA obligatorio</span>
              </label>
            </div>
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!vpn.remote_access?.ad_integrated}
                       onChange={e => setRA('ad_integrated', e.target.checked)} />
                <span>Integrado con AD/RADIUS</span>
              </label>
            </div>
            <div className="col-md-3">
              <label className="form-label">Usuarios máx.</label>
              <input type="number" className="form-control form-control-sm"
                     value={vpn.remote_access?.max_users ?? ''}
                     onChange={e => setRA('max_users', e.target.value === '' ? '' : Number(e.target.value))} />
            </div>
            <div className="col-md-9">
              <label className="form-label">Pool de IPs (CIDR)</label>
              <input className="form-control form-control-sm"
                     placeholder="172.16.100.0/24"
                     value={vpn.remote_access?.client_subnet || ''}
                     onChange={e => setRA('client_subnet', e.target.value)} />
            </div>
          </div>
        ) : (
          vpn.remote_access?.enabled ? (
            <div style={{ fontSize: '0.86rem', color: TOPOLOGY_PALETTE.textStrong }}>
              <strong>{PROTOCOLS.find(p => p.value === vpn.remote_access.protocol)?.label}</strong>
              {vpn.remote_access.mfa && <> · MFA</>}
              {vpn.remote_access.ad_integrated && <> · AD/RADIUS</>}
              {vpn.remote_access.max_users && <> · {vpn.remote_access.max_users} usuarios</>}
              {vpn.remote_access.client_subnet && <> · Pool {vpn.remote_access.client_subnet}</>}
            </div>
          ) : (
            <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>
              Sin acceso remoto habilitado.
            </div>
          )
        )}
      </SectionBox>

      {/* ── WAN / Failover ── */}
      <SectionBox title="WAN y operadores" accent={TOPOLOGY_PALETTE.haAccent}>
        {editMode ? (
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-check d-flex align-items-center gap-2" style={{ gap: 6 }}>
                <input type="checkbox" className="form-check-input"
                       checked={!!vpn.wan?.dual_wan}
                       onChange={e => setWan('dual_wan', e.target.checked)} />
                <span>Dual WAN</span>
              </label>
            </div>
            <div className="col-md-3">
              <label className="form-label">Operador principal</label>
              <input className="form-control form-control-sm"
                     value={vpn.wan?.primary_isp || ''}
                     onChange={e => setWan('primary_isp', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Operador backup</label>
              <input className="form-control form-control-sm"
                     value={vpn.wan?.backup_isp || ''}
                     onChange={e => setWan('backup_isp', e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Modo failover</label>
              <select className="form-select form-select-sm" value={vpn.wan?.failover_mode || 'active_passive'}
                      onChange={e => setWan('failover_mode', e.target.value)}>
                {FAILOVER.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '0.86rem', color: TOPOLOGY_PALETTE.textStrong }}>
            {vpn.wan?.dual_wan ? 'Dual WAN' : 'WAN única'}
            {vpn.wan?.primary_isp && <> · principal <strong>{vpn.wan.primary_isp}</strong></>}
            {vpn.wan?.backup_isp  && <> · backup <strong>{vpn.wan.backup_isp}</strong></>}
            {vpn.wan?.dual_wan && vpn.wan?.failover_mode && <> · {FAILOVER.find(f => f.value === vpn.wan.failover_mode)?.label}</>}
          </div>
        )}
      </SectionBox>

      {/* ── Features clave ── */}
      <SectionBox title="Características clave" accent={TOPOLOGY_PALETTE.successAccent}>
        {editMode ? (
          <textarea
            className="form-control"
            rows={4}
            placeholder="Una característica por línea (ej. VPN Site-to-Site IPSec con cifrado AES-256, MFA obligatorio, Integración AD/RADIUS, Monitorización 24/7…)"
            value={(vpn.features || []).join('\n')}
            onChange={e => set('features', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(vpn.features || []).length === 0 && (
              <div style={{ fontSize: '0.82rem', color: TOPOLOGY_PALETTE.textMuted, fontStyle: 'italic' }}>Sin características.</div>
            )}
            {(vpn.features || []).map((f, i) => (
              <span key={i} style={{ background: 'rgba(16,185,129,.12)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,.35)', padding: '3px 8px', borderRadius: 12, fontSize: '0.78rem' }}>
                {f}
              </span>
            ))}
          </div>
        )}
      </SectionBox>
    </div>
  );
}

function endpointLabel(ref, vpn) {
  if (!ref) return '—';
  const [kind, idx] = ref.split(':');
  const i = Number(idx);
  if (kind === 'gw'   && vpn.gateways?.[i])  return `GW · ${vpn.gateways[i].name  || `Gateway ${i+1}`}`;
  if (kind === 'site' && vpn.sites?.[i])     return `Sede · ${vpn.sites[i].name    || `Sede ${i+1}`}`;
  if (kind === 'prov' && vpn.providers?.[i]) return `Ext · ${vpn.providers[i].name || `Proveedor ${i+1}`}`;
  return ref;
}
